// Direct-to-S3 uploads for prepared media (Create-Example, User Avatar Upload and Change
// Video Thumbnail modals): prepare → PUT each role's blob to its presigned URL → the caller
// finalizes with an Inertia visit carrying the media upload id.
//
// Prepare is an Inertia POST that asks only for the `prepared_upload` prop: the server renders
// the presigned URLs into that response rather than a redirect's flash, which would carry them
// through the 4KB cookie session (AdminSite::PreparedMediaUploadRows). The S3 PUT is outside
// the app altogether; XHR rather than fetch for it because fetch cannot report upload progress.
//
// The presigned URL signs no Content-Type or length conditions — the server
// sniffs the MIME type from the stored bytes at finalize — so the request sends
// no custom headers and the URL is used verbatim.
import { router } from '@inertiajs/svelte'

export class PresignedPutError extends Error {
  // kind: 'expired' | 'network' | 'rejected'
  constructor(kind, message, status) {
    super(message)
    this.name = 'PresignedPutError'
    this.kind = kind
    this.status = status
  }
}

export const putPresigned = (url, blob, { onProgress } = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1)
        resolve()
      } else if (xhr.status === 403) {
        // Presigned URL expired (15-minute TTL) — the caller should re-prepare.
        reject(new PresignedPutError('expired', 'Upload URL expired', xhr.status))
      } else if (xhr.status >= 500) {
        reject(new PresignedPutError('network', `Storage error (${xhr.status})`, xhr.status))
      } else {
        reject(new PresignedPutError('rejected', `Upload rejected (${xhr.status})`, xhr.status))
      }
    }
    xhr.onerror = () => reject(new PresignedPutError('network', 'Network error during upload'))
    xhr.onabort = () => reject(new PresignedPutError('network', 'Upload aborted'))

    xhr.send(blob)
  })

const retryDelay = () => 300 + Math.random() * 500

// One automatic retry on transient failures (network drop, 5xx). Expired URLs
// and hard rejections surface immediately for the caller to handle.
export const putPresignedWithRetry = async (url, blob, options = {}) => {
  try {
    await putPresigned(url, blob, options)
  } catch (error) {
    if (!(error instanceof PresignedPutError) || error.kind !== 'network') throw error
    await new Promise((resolve) => setTimeout(resolve, retryDelay()))
    await putPresigned(url, blob, options)
  }
}

// PUTs one blob per role in parallel, reporting byte-weighted combined progress.
// The role sets must match exactly — an unexpected or missing role means client
// and server disagree about the quest's capture mode.
export const putRoleFiles = async (files, blobs, onProgress) => {
  const serverRoles = files.map((file) => file.role).sort()
  const localRoles = Object.keys(blobs).sort()
  if (serverRoles.join(',') !== localRoles.join(',')) {
    throw new PresignedPutError(
      'rejected',
      `Upload roles mismatch: server expects [${serverRoles}], client has [${localRoles}]`
    )
  }

  const totalBytes = Object.values(blobs).reduce((sum, blob) => sum + blob.size, 0)
  const uploadedByRole = {}

  const reportProgress = () => {
    if (!onProgress || totalBytes === 0) return
    const uploaded = Object.values(uploadedByRole).reduce((sum, bytes) => sum + bytes, 0)
    onProgress(uploaded / totalBytes)
  }

  await Promise.all(
    files.map(({ role, upload_url }) => {
      const blob = blobs[role]
      return putPresignedWithRetry(upload_url, blob, {
        onProgress: (fraction) => {
          uploadedByRole[role] = fraction * blob.size
          reportProgress()
        }
      })
    })
  )
}

export class PrepareFailedError extends Error {
  // code: the server's error class name (e.g. 'TooManyActiveUploadsError'), when it sent one.
  constructor(message, code = null) {
    super(message)
    this.name = 'PrepareFailedError'
    this.code = code
  }
}

const inertiaPost = (url, data, options) => router.post(url, data, options)

// POSTs to a prepare endpoint and resolves to its `prepared_upload` ({ id, expires_at,
// files: [{ role, upload_url }] }). Every failure rejects with a PrepareFailedError.
export const requestPreparedUpload = (path, body = {}, { post = inertiaPost } = {}) =>
  new Promise((resolve, reject) => {
    let settled = false
    const settle = (callback) => {
      settled = true
      callback()
    }
    post(path, body, {
      only: ['prepared_upload'],
      async: true,
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true,
      showProgress: false,
      onSuccess: (page) =>
        settle(() => {
          const upload = page.props.prepared_upload
          upload ? resolve(upload) : reject(new PrepareFailedError('The server did not return an upload.'))
        }),
      onError: (errors) =>
        settle(() => reject(new PrepareFailedError(errors.prepared_upload || errors.base || 'The server rejected the upload.', errors.prepared_upload_code ?? null))),
      onFinish: () => {
        if (!settled) reject(new PrepareFailedError('Network error — check your connection and retry.'))
      }
    })
  })

// prepare → parallel presigned PUTs → the media upload id to finalize. Expired URLs
// (15-minute TTL) get one re-prepare + full re-PUT; re-PUTting an already-stored temp
// object is harmless.
export const uploadPrepared = async (prepare, blobs, { onProgress, put = putRoleFiles } = {}) => {
  let upload = await prepare()
  try {
    await put(upload.files, blobs, onProgress)
  } catch (error) {
    if (!(error instanceof PresignedPutError) || error.kind !== 'expired') throw error
    upload = await prepare()
    await put(upload.files, blobs, onProgress)
  }
  return upload.id
}
